import React, { FC } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';

import { MicrosoftCustomExpirationBanner_EmployeeFragment } from 'src/generated/mloop-graphql';

import AppWideBanner from '../AppWideBanner';

import {
  ExpirationInfo,
  ExpirationStatus,
  generateCustomIntegrationMessageI18n,
  getBannerConfig,
  getExpirationInfo,
} from './utils';

interface EmployeeExpirationInfo extends ExpirationInfo {
  email: string;
}

interface Props {
  employees: MicrosoftCustomExpirationBanner_EmployeeFragment[];
}

const MicrosoftCustomExpirationBanner: FC<Props> = ({ employees }) => {
  const { t } = useTranslation();
  if (!employees?.length) {
    return null;
  }

  // Collect expiration info for all employees
  const employeeExpirations: EmployeeExpirationInfo[] = employees
    .map((employee) => {
      const integration = employee.integrations?.microsoftCustomEmployee;
      if (!integration) {
        return null;
      }

      const expirationInfo = getExpirationInfo(integration.updatedAt);
      return {
        email: employee.email,
        ...expirationInfo,
      };
    })
    .filter((info): info is EmployeeExpirationInfo => info !== null && info.status !== 'none');

  if (employeeExpirations.length === 0) {
    return null;
  }

  // Determine most critical status (expired > warning)
  const hasExpired = employeeExpirations.some((info) => info.status === 'expired');
  const mostCriticalStatus: ExpirationStatus = hasExpired ? 'expired' : 'warning';

  // Get affected emails for the most critical status
  const affectedEmployees = employeeExpirations.filter((info) => info.status === mostCriticalStatus);
  const emails = affectedEmployees.map((info) => info.email);

  // Use the minimum days remaining for the message
  const minDaysRemaining = Math.min(...affectedEmployees.map((info) => info.daysRemaining));

  const bannerConfig = getBannerConfig(mostCriticalStatus);
  if (!bannerConfig) {
    return null;
  }

  const message = generateCustomIntegrationMessageI18n(t, mostCriticalStatus, minDaysRemaining, emails);

  return <AppWideBanner text={message} backgroundColor={bannerConfig.backgroundColor} color={bannerConfig.color} />;
};

export const MicrosoftCustomExpirationBannerFragments = {
  employee: gql`
    fragment MicrosoftCustomExpirationBanner_employee on Employee {
      id
      email
      integrations {
        id
        microsoftCustomEmployee {
          updatedAt
        }
      }
    }
  `,
};

export default MicrosoftCustomExpirationBanner;
